import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWheelScroll } from '@/hooks/useWheelScroll';

interface TimeSelectProps {
  value: string;           // "HH:mm" 24hr format
  onChange: (time: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** When set, this becomes an END-time selector: options roll forward from
   *  this start time (start+15 min first), each showing its running duration
   *  (e.g. "1:00 AM · 3 hr"). No "next day" text — the clock time + duration
   *  convey an overnight end. Capped at +24h; longer spans use "All day". */
  startTime?: string;
  /** Accessible name for the trigger (the visible layout has no text label). */
  ariaLabel?: string;
}

/** Generate 96 time slots in 15-min intervals */
function generateTimeSlots(): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
      slots.push({ value, label });
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

/** Convert "HH:mm" to display string like "2:00 PM" */
function formatTimeDisplay(time: string): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time;
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** Format a minute count to a human string: "30 min", "1 hr", "1 hr 30 min" */
function formatDuration(minutes: number): string {
  if (minutes < 0) return '';
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hrs} hr`;
  return `${hrs} hr ${mins} min`;
}

function minutesOfDay(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** END-time options rolling forward from a start time.
 *  15-min steps up to +12h, then 30-min steps up to +24h (inclusive).
 *  No "next day" text: the absolute clock time plus the running duration
 *  convey an overnight end. Spans over 24h are meant to be "All day" events. */
function generateEndSlots(startTime: string): { value: string; label: string; duration: string }[] {
  const startMin = minutesOfDay(startTime);
  const out: { value: string; label: string; duration: string }[] = [];
  const add = (dur: number) => {
    const abs = (startMin + dur) % (24 * 60);
    const value = `${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
    out.push({ value, label: formatTimeDisplay(value), duration: formatDuration(dur) });
  };
  for (let dur = 15; dur <= 12 * 60; dur += 15) add(dur);
  for (let dur = 12 * 60 + 30; dur <= 24 * 60; dur += 30) add(dur);
  return out;
}

export { formatTimeDisplay, formatDuration, generateEndSlots };

export function TimeSelect({
  value,
  onChange,
  disabled = false,
  placeholder = 'Time',
  className,
  startTime,
  ariaLabel,
}: TimeSelectProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const selectedRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [listEl, setListEl] = useState<HTMLDivElement | null>(null);

  // End selector rolls forward from the start time; start selector is a full day.
  const slots = useMemo<{ value: string; label: string; duration?: string }[]>(
    () => (startTime ? generateEndSlots(startTime) : TIME_SLOTS),
    [startTime]
  );

  // On open: focus the filter, and position the list. The end selector opens at
  // the top (shortest duration first); the start selector centers on the
  // currently selected time.
  useEffect(() => {
    if (!open) return;
    setFilter('');
    const t = setTimeout(() => {
      inputRef.current?.focus();
      if (startTime) {
        listEl?.scrollTo({ top: 0 });
      } else {
        selectedRef.current?.scrollIntoView({ block: 'center' });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [open, startTime, listEl]);

  const filteredSlots = useMemo(() => {
    if (!filter) return slots;
    const lower = filter.toLowerCase().replace(/\s/g, '');
    return slots.filter(s =>
      s.label.toLowerCase().replace(/\s/g, '').includes(lower) ||
      s.value.includes(lower)
    );
  }, [filter, slots]);

  const displayValue = value ? formatTimeDisplay(value) : '';

  // Wheel scrolling inside a modal Dialog needs a native listener; see useWheelScroll.
  // One ref feeds both the hook and listEl (used by the open effect above).
  const wheelRef = useWheelScroll();
  const listRef = useCallback(
    (node: HTMLDivElement | null) => {
      setListEl(node);
      wheelRef(node);
    },
    [wheelRef]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'justify-start text-left font-normal w-[130px]',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4 shrink-0" />
          {displayValue || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <div className="p-2 border-b">
          <input
            ref={inputRef}
            type="text"
            aria-label="Filter times"
            className="w-full px-2 py-1 text-sm border rounded outline-none focus:ring-1 focus:ring-ring"
            placeholder="Type a time..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filteredSlots.length > 0) {
                onChange(filteredSlots[0].value);
                setOpen(false);
              }
              if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
        </div>
        <div ref={listRef} className="max-h-[240px] overflow-y-auto p-1">
          {filteredSlots.map((slot) => (
            <button
              key={slot.value}
              ref={slot.value === value ? selectedRef : undefined}
              type="button"
              className={cn(
                'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                slot.value === value && 'bg-accent text-accent-foreground font-medium'
              )}
              onClick={() => {
                onChange(slot.value);
                setOpen(false);
              }}
            >
              <span>{slot.label}</span>
              {slot.duration && (
                <span className="text-xs text-muted-foreground">{slot.duration}</span>
              )}
            </button>
          ))}
          {filteredSlots.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">No matching times</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
